/**
 * Composant pour gérer les catégories de produits
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categoriesAPI } from '@/utils/categoriesAPI';
import type { ProductCategory } from '@/types/models';
import { FolderOpen, Edit, Trash2, Plus, X, ChevronRight } from 'lucide-react';
import { notifications } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';
import { logger } from '@/utils/logger';

interface CategoryManagerProps {
  onCategoryCreated?: (category: ProductCategory) => void;
  onCategoryUpdated?: (category: ProductCategory) => void;
}

// Icônes disponibles pour les catégories (nom Lucide)
const AVAILABLE_ICONS = [
  { value: 'FolderOpen', label: 'Dossier' },
  { value: 'Wrench', label: 'Clé' },
  { value: 'Hammer', label: 'Marteau' },
  { value: 'Package', label: 'Colis' },
  { value: 'Tool', label: 'Outil' },
  { value: 'Paintbrush', label: 'Pinceau' },
  { value: 'Zap', label: 'Éclair' },
  { value: 'Home', label: 'Maison' },
  { value: 'Building', label: 'Bâtiment' },
  { value: 'ShoppingCart', label: 'Panier' },
];

// Couleurs suggérées (Volvo theme)
const SUGGESTED_COLORS = [
  '#0D9488', // teal-600
  '#14B8A6', // teal-500
  '#2DD4BF', // teal-400
  '#10B981', // emerald-500
  '#059669', // emerald-600
  '#06B6D4', // cyan-500
  '#0891B2', // cyan-600
  '#3B82F6', // blue-500
];

export function CategoryManager({ onCategoryCreated, onCategoryUpdated }: CategoryManagerProps) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: '',
    color: '#0D9488',
    icon: 'FolderOpen',
    order: 0,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      logger.error('Erreur lors du chargement des catégories:', error);
      notifications.error(t('categories.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        parentId: category.parentId || '',
        color: category.color || '#0D9488',
        icon: category.icon || 'FolderOpen',
        order: category.order || 0,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        parentId: '',
        color: '#0D9488',
        icon: 'FolderOpen',
        order: 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        const updated = await categoriesAPI.update(editingCategory.id, {
          name: formData.name,
          description: formData.description || undefined,
          parentId: formData.parentId || undefined,
          color: formData.color,
          icon: formData.icon,
          order: formData.order,
        });
        notifications.success(t('categories.updateSuccess'));
        onCategoryUpdated?.(updated);
      } else {
        const created = await categoriesAPI.create({
          name: formData.name,
          description: formData.description || undefined,
          parentId: formData.parentId || undefined,
          color: formData.color,
          icon: formData.icon,
          order: formData.order,
        });
        notifications.success(t('categories.createSuccess'));
        onCategoryCreated?.(created);
      }
      handleCloseModal();
      loadCategories();
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde de la catégorie:', error);
      notifications.error(t('categories.errors.saveFailed'));
    }
  };

  const handleDelete = async (categoryId: string, categoryName: string) => {
    const confirmed = await confirm({
      title: t('categories.deleteTitle'),
      message: t('categories.deleteConfirm', { name: categoryName }),
      variant: 'danger',
      confirmText: t('common.actions.delete'),
      cancelText: t('common.actions.cancel'),
    });
    if (!confirmed) {
      return;
    }

    try {
      await categoriesAPI.delete(categoryId);
      notifications.success(t('categories.deleteSuccess'));
      loadCategories();
    } catch (error) {
      logger.error('Erreur lors de la suppression de la catégorie:', error);
      notifications.error(t('categories.errors.deleteFailedProducts'));
    }
  };

  // Obtenir le chemin hiérarchique d'une catégorie
  const getCategoryPath = (category: ProductCategory): string => {
    if (!category.parentId) return category.name;
    const parent = categories.find(c => c.id === category.parentId);
    if (!parent) return category.name;
    return `${getCategoryPath(parent)} > ${category.name}`;
  };

  // Filtrer les catégories disponibles comme parent (éviter boucle)
  const getAvailableParents = (): ProductCategory[] => {
    if (!editingCategory) return categories;
    // Exclure la catégorie en cours d'édition et ses descendants
    return categories.filter(c => c.id !== editingCategory.id);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="w-16 h-16 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8 animate-fade-in-up">
        <div>
          <h1 className="text-5xl font-light tracking-tight text-gray-900 mb-2">
            {t('categories.title')}
          </h1>
          <p className="text-base text-gray-500 font-light tracking-wide">
            {categories.length} {t('categories.title').toLowerCase()}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-3.5 rounded-2xl font-bold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap shadow-lg flex items-center gap-2.5"
        >
          <Plus className="w-5 h-5" />
          {t('categories.create')}
        </button>
      </div>

      {/* Categories List */}
      {categories.length === 0 ? (
        <div className="bg-white/20 backdrop-blur-xl rounded-3xl p-12 text-center border-2 border-dashed border-gray-300/50 animate-fade-in-up">
          <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg text-gray-600 mb-4">{t('categories.empty')}</p>
          <button
            onClick={() => handleOpenModal()}
            className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-2.5 rounded-xl font-bold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 shadow-lg inline-flex items-center gap-2"
          >
            {t('categories.createFirst')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className="group bg-white/20 backdrop-blur-xl rounded-3xl p-8 hover:bg-white/70 hover:-translate-y-1 transition-all duration-500 animate-fade-in-up shadow-xl border border-white/30"
              style={{
                animationDelay: `${index * 100}ms`,
                boxShadow: '0 10px 40px rgba(0,0,0,0.02), 0 2px 15px rgba(0,0,0,0.01)'
              }}
            >
              <div className="flex flex-col h-full">
                {/* Content */}
                <div className="flex-1 mb-4">
                  <div className="flex items-start gap-3 mb-3">
                    {/* Color indicator */}
                    {category.color && (
                      <div
                        className="w-3 h-3 rounded-full border-2 border-white shadow-sm mt-1.5"
                        style={{ backgroundColor: category.color }}
                      />
                    )}

                    {/* Icon */}
                    {category.icon && (
                      <div className="p-3 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 text-teal-600 shadow-sm">
                        {category.icon === 'FolderOpen' && <FolderOpen className="w-5 h-5" />}
                      </div>
                    )}

                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-900 mb-1">
                        {category.name}
                      </h3>

                      {/* Parent indicator */}
                      {category.parentId && (
                        <span className="flex items-center text-xs text-teal-600 font-medium">
                          <ChevronRight className="w-3 h-3" />
                          {t('categories.subcategory')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {category.description && (
                    <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                      {category.description}
                    </p>
                  )}

                  {/* Hierarchy path */}
                  {category.parentId && (
                    <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-2 py-1 inline-block">
                      {getCategoryPath(category)}
                    </p>
                  )}

                  {/* Product count */}
                  {category._count && (
                    <p className="text-xs text-teal-600 font-medium mt-2">
                      {category._count.Product} produit{category._count.Product > 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200/50">
                  <button
                    onClick={() => handleOpenModal(category)}
                    className="px-4 py-2 rounded-xl bg-teal-50/50 text-teal-700 hover:bg-teal-100 hover:scale-105 transition-all duration-300 flex items-center gap-1.5 text-sm font-semibold"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    {t('common.actions.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(category.id, category.name)}
                    className="px-4 py-2 rounded-xl bg-red-50/50 text-red-700 hover:bg-red-100 hover:scale-105 transition-all duration-300 flex items-center gap-1.5 text-sm font-semibold"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {t('common.actions.delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-scale-in">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-8 pb-6 border-b border-gray-200/50">
              <h3 className="text-3xl font-light tracking-tight text-gray-900">
                {editingCategory ? t('categories.editTitle') : t('categories.create')}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                    {t('categories.fields.name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                    {t('categories.fields.description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows={3}
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                    {t('categories.fields.parent')}
                  </label>
                  <select
                    value={formData.parentId}
                    onChange={(e) =>
                      setFormData({ ...formData, parentId: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  >
                    <option value="">{t('categories.noParent')}</option>
                    {getAvailableParents().map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {getCategoryPath(cat)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                      {t('categories.fields.color')}
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) =>
                          setFormData({ ...formData, color: e.target.value })
                        }
                        className="h-12 w-24 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-teal-400"
                      />
                      <div className="flex gap-1.5 flex-wrap">
                        {SUGGESTED_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setFormData({ ...formData, color })}
                            className="w-9 h-9 rounded-lg border-2 border-gray-200 hover:border-teal-400 hover:scale-110 transition-all duration-300"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                      {t('categories.fields.icon')}
                    </label>
                    <select
                      value={formData.icon}
                      onChange={(e) =>
                        setFormData({ ...formData, icon: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    >
                      {AVAILABLE_ICONS.map((icon) => (
                        <option key={icon.value} value={icon.value}>
                          {icon.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2.5">
                    {t('categories.fields.displayOrder')}
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                    }
                    className="w-full px-4 py-3 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded-lg px-3 py-2">
                    {t('categories.displayOrderHelp')}
                  </p>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200/50">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-white/60 backdrop-blur-xl text-gray-700 px-6 py-2.5 rounded-xl font-semibold border border-gray-200/60 hover:bg-white/80 transition-all duration-300"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-2.5 rounded-xl font-bold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 shadow-lg"
                >
                  {editingCategory ? t('common.actions.save') : t('common.actions.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
